// src/components/AdminRoute.js
import { useEffect, useState, useRef } from "react";
import { Navigate } from "react-router-dom";
import { getUserRole } from "../pages/authUtils";
import { getAuth } from "firebase/auth";

const AdminRoute = ({ children }) => {
  const [allowed, setAllowed] = useState(null);
  const [isSecure, setIsSecure] = useState(true);
  const lastActivityRef = useRef(Date.now());
  const sessionTimeoutRef = useRef(null);
  const revalidationIntervalRef = useRef(null);

  // Security: Console detection
  useEffect(() => {
    const detectDevTools = () => {
      const start = Date.now();
      debugger;
      const end = Date.now();
      if (end - start > 100) {
        console.warn('Developer tools detected! Redirecting for security.');
        setIsSecure(false);
        return true;
      }
      return false;
    };

    // Check every 1 second
    const devToolsInterval = setInterval(() => {
      if (detectDevTools()) {
        clearInterval(devToolsInterval);
      }
    }, 1000);

    return () => clearInterval(devToolsInterval);
  }, []);

  // Security: Tab focus monitoring
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        // Tab is hidden, start security countdown
        setTimeout(() => {
          if (document.hidden) {
            console.warn('Tab was inactive too long. Security logout.');
            setIsSecure(false);
          }
        }, 30000); // 30 seconds
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, []);

  // Security: Session timeout
  useEffect(() => {
    const resetTimeout = () => {
      lastActivityRef.current = Date.now();
      if (sessionTimeoutRef.current) {
        clearTimeout(sessionTimeoutRef.current);
      }
      sessionTimeoutRef.current = setTimeout(() => {
        console.warn('Session timeout. Logging out for security.');
        setIsSecure(false);
      }, 15 * 60 * 1000); // 15 minutes
    };

    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart'];
    events.forEach(event => {
      document.addEventListener(event, resetTimeout, true);
    });

    resetTimeout(); // Initialize timeout

    return () => {
      events.forEach(event => {
        document.removeEventListener(event, resetTimeout, true);
      });
      if (sessionTimeoutRef.current) {
        clearTimeout(sessionTimeoutRef.current);
      }
    };
  }, []);

  // Security: Periodic re-validation
  useEffect(() => {
    const auth = getAuth();
    
    const validateUser = async (user) => {
      if (user) {
        try {
          // Force token refresh
          await user.getIdToken(true);
          const role = await getUserRole(user.uid);
          const isAdmin = role === "admin";
          setAllowed(isAdmin);
          
          if (!isAdmin) {
            console.warn('Admin privileges revoked. Redirecting.');
            setIsSecure(false);
          }
        } catch (error) {
          console.error('Admin validation failed:', error);
          setAllowed(false);
          setIsSecure(false);
        }
      } else {
        setAllowed(false);
        setIsSecure(false);
      }
    };

    const unsubscribe = auth.onAuthStateChanged(validateUser);

    // Re-validate every 5 minutes
    revalidationIntervalRef.current = setInterval(() => {
      const user = auth.currentUser;
      if (user) {
        validateUser(user);
      }
    }, 5 * 60 * 1000);

    return () => {
      unsubscribe();
      if (revalidationIntervalRef.current) {
        clearInterval(revalidationIntervalRef.current);
      }
    };
  }, []);

  // Security: Disable right-click and common shortcuts
  useEffect(() => {
    const handleContextMenu = (e) => {
      e.preventDefault();
      console.warn('Right-click disabled for security.');
    };

    const handleKeyDown = (e) => {
      // Disable F12, Ctrl+Shift+I, Ctrl+Shift+J, Ctrl+U, Ctrl+Shift+C
      if (
        e.key === 'F12' ||
        (e.ctrlKey && e.shiftKey && (e.key === 'I' || e.key === 'J' || e.key === 'C')) ||
        (e.ctrlKey && e.key === 'u')
      ) {
        e.preventDefault();
        console.warn('Developer shortcuts disabled for security.');
        setIsSecure(false);
      }
    };

    document.addEventListener('contextmenu', handleContextMenu);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('contextmenu', handleContextMenu);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  if (!isSecure) {
    return <Navigate to="/HomeScreen" replace />;
  }

  if (allowed === null) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        background: '#f0f0f0'
      }}>
        <div>Verifying admin access...</div>
      </div>
    );
  }

  return allowed ? children : <Navigate to="/HomeScreen" replace />;
};

export default AdminRoute;
